import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTimes, faSave } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch } from '../../utils/http';
import PasswordResetBox from './PasswordResetBox';

interface Props {
  userId: string;
  onClose: () => void;
  onSaved: () => void;
}

type Tab = 'details' | 'security';

const inputClass = 'w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500';

/** Admin editor for a trainer/assessor: account details, employment status and password. */
export default function EditTrainerModal({ userId, onClose, onSaved }: Props) {
  const { accessToken } = useAuth();
  const [tab, setTab] = useState<Tab>('details');
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    isActive: true,
    employeeId: '',
    specialization: '',
    biography: '',
    status: 0,
  });

  useEffect(() => {
    (async () => {
      const res = await authFetch<typeof form>(accessToken, `/api/admin/users/instructors/${userId}`);
      if (res.ok && res.data) {
        const d = res.data;
        setForm({
          firstName: d.firstName,
          lastName: d.lastName,
          email: d.email,
          isActive: d.isActive,
          employeeId: d.employeeId,
          specialization: d.specialization ?? '',
          biography: d.biography ?? '',
          status: d.status,
        });
        setLoaded(true);
      } else {
        setError(res.message || 'Failed to load the trainer.');
      }
    })();
  }, [accessToken, userId]);

  const set = (name: string, value: string | boolean | number) => {
    setNotice('');
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const save = async () => {
    setError('');
    setNotice('');
    setIsSaving(true);
    const res = await authFetch(accessToken, `/api/admin/users/instructors/${userId}`, { method: 'PUT', body: form });
    setIsSaving(false);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setNotice('Trainer updated.');
    onSaved();
  };

  return (
    <div className="fixed inset-0 bg-brand-950/40 z-50 flex items-start justify-center px-4 py-8 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-xl p-6 my-auto">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-bold text-brand-950">Edit trainer/assessor</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-canvas-100 rounded-lg text-brand-900/60">
            <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
          </button>
        </div>

        {!loaded && !error && (
          <div className="flex justify-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-brand-600 animate-spin" />
          </div>
        )}
        {!loaded && error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

        {loaded && (
          <>
            <div className="flex gap-1 border-b border-canvas-300 mb-5">
              {(['details', 'security'] as Tab[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                    tab === t ? 'border-brand-600 text-brand-700' : 'border-transparent text-brand-900/60 hover:text-brand-900'
                  }`}
                >
                  {t === 'details' ? 'Details' : 'Password'}
                </button>
              ))}
            </div>

            {tab === 'details' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-brand-950 mb-2">First name</label>
                    <input className={inputClass} value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-brand-950 mb-2">Last name</label>
                    <input className={inputClass} value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">Email (used to sign in)</label>
                  <input type="email" className={inputClass} value={form.email} onChange={(e) => set('email', e.target.value)} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-brand-950 mb-2">Employee ID</label>
                    <input className={inputClass} value={form.employeeId} onChange={(e) => set('employeeId', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-brand-950 mb-2">Specialization</label>
                    <input className={inputClass} value={form.specialization} onChange={(e) => set('specialization', e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">Biography</label>
                  <textarea rows={3} className={`${inputClass} resize-none`} value={form.biography} onChange={(e) => set('biography', e.target.value)} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-brand-950 mb-2">Employment status</label>
                    <select className={`${inputClass} bg-white`} value={form.status} onChange={(e) => set('status', parseInt(e.target.value, 10))}>
                      <option value={0}>Active</option>
                      <option value={1}>Inactive</option>
                      <option value={2}>On leave</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-brand-950 mb-2">Can sign in</label>
                    <label className="flex items-center gap-2 text-sm text-brand-900/70 h-10">
                      <input type="checkbox" checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} />
                      Account enabled
                    </label>
                  </div>
                </div>

                {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}
                {notice && <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">{notice}</p>}

                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={onClose} className="px-5 py-2 border border-canvas-300 text-brand-950 font-semibold rounded-lg hover:bg-canvas-100 transition-colors text-sm">
                    Close
                  </button>
                  <button
                    onClick={save}
                    disabled={isSaving}
                    className="px-5 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
                  >
                    <FontAwesomeIcon icon={isSaving ? faSpinner : faSave} className={`h-3.5 w-3.5 ${isSaving ? 'animate-spin' : ''}`} />
                    Save changes
                  </button>
                </div>
              </div>
            )}

            {tab === 'security' && <PasswordResetBox userId={userId} />}
          </>
        )}
      </div>
    </div>
  );
}
